import { Inject, Injectable } from '@angular/core';
import { COLLECTIONS, ENVIRONMENT } from '../core.constants';
import { Preference } from '../models/preference.model';
import { AuthService } from '../services/auth.service';
import { FirestoreService } from '../services/firestore.service';
import { Observable } from 'rxjs';
import { first, map, shareReplay, switchMap } from 'rxjs/operators';
import { TranslocoService } from '@ngneat/transloco';

const DEFAULT_LANGUAGE = 'en';

@Injectable({
  providedIn: 'root',
})
export class UserPreferencesService {
  private collection = COLLECTIONS.USER_PREFERENCES;

  /**
   * Firestore trabaja con caché en disco, así que al arrancar responde primero desde el móvil y
   * luego desde el servidor. Si en esa primera respuesta la caché todavía no tiene las preferencias
   * (recién instalada, recién actualizada o sin cobertura en ese instante), `getDocument` lanzaba un
   * error; como este observable va con `shareReplay`, ese error se quedaba guardado para el resto de
   * la sesión y ya no llegaba nunca el idioma real, aunque el servidor lo devolviese un segundo
   * después. Eso dejaba en blanco el perfil y la pantalla de formularios.
   *
   * Ahora una respuesta sin datos devuelve preferencias por defecto con el idioma que la persona ya
   * está viendo. Cuando llega la respuesta buena del servidor, se emite y todo se actualiza solo.
   * Esto cubre también a quien nunca haya llegado a tener documento de preferencias.
   */
  public myPreferences$: Observable<Preference> = this.auth.uid$.pipe(
    switchMap((uid) =>
      this.firestore
        .getDocumentOrNull<Preference>(this.collection, uid)
        .pipe(map((pref) => this.withDefaults(pref, uid))),
    ),
    shareReplay(1),
  );

  public myLanguage$: Observable<string> = this.myPreferences$.pipe(map((pref) => pref.language));
  public myMessagesLanguage$: Observable<string | undefined> = this.myPreferences$.pipe(map((pref) => pref.messagesLanguage));

  constructor(
    private firestore: FirestoreService,
    private auth: AuthService,
    private translocoService: TranslocoService,
    @Inject(ENVIRONMENT) private environment: any,
  ) {}

  /** Idioma que el usuario está viendo ahora mismo, como respaldo cuando Firestore no lo tiene. */
  private currentLanguage(): string {
    let storedLang: string | null = null;
    try {
      storedLang = localStorage.getItem('lang');
    } catch {
      storedLang = null;
    }
    return this.translocoService.getActiveLang() || storedLang || DEFAULT_LANGUAGE;
  }

  private withDefaults(pref: Preference | null, uid: string): Preference {
    const fallbackLang = this.currentLanguage();
    return {
      emails: true,
      notifications: true,
      ...(pref ?? {}),
      id: pref?.id ?? uid,
      language: pref?.language || fallbackLang,
      messagesLanguage: pref?.messagesLanguage || pref?.language || fallbackLang,
    };
  }

  createOrUpdatePreferences(data: Partial<Preference>): Promise<void> {
    return this.auth.uid$
      .pipe(
        first(),
        switchMap((uid) => this.firestore.createOrUpdate(this.collection, uid, data)),
      )
      .toPromise();
  }
}
